import { useAuth } from "@/auth/auth";
import { useUser } from "@/client/backend/user";
import { useEffect } from "react";

declare global {
  interface Window {
    pylon?: {
      chat_settings: {
        app_id: string;
        email: string;
        name: string;
        // Pylon account external ID; we register organizations under their TID.
        account_external_id: string;
      };
    };
    Pylon?: ((...args: unknown[]) => void) & { q: unknown[]; e: (args: unknown) => void };
  }
}

const PYLON_APP_ID = import.meta.env.VITE_PYLON_APP_ID?.trim();
const PYLON_SCRIPT_ID = "pylon-widget";

// Pylon support chat widget. Pylon requires the visitor's email and name, so
// this renders only inside authenticated routes and loads the SDK once the
// user profile is known. Widget appearance and behavior are configured in the
// Pylon dashboard, not here.
export const PylonWidget = () => {
  const { userTid, organizationTid } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    if (!PYLON_APP_ID || user === "loading") {
      return;
    }
    // chat_settings must exist before the SDK script runs; the SDK reads them
    // on load.
    window.pylon = {
      chat_settings: {
        app_id: PYLON_APP_ID,
        email: user.emailAddress,
        name: user.displayName || user.name,
        account_external_id: organizationTid,
      },
    };
    if (!document.getElementById(PYLON_SCRIPT_ID)) {
      // Command queue from Pylon's install snippet: calls made before the SDK
      // loads are buffered and replayed by the SDK.
      const queue = ((...args: unknown[]) => {
        queue.e(args);
      }) as NonNullable<Window["Pylon"]>;
      queue.q = [];
      queue.e = (args: unknown) => {
        queue.q.push(args);
      };
      window.Pylon = queue;
      // Guard against StrictMode double-mount and route remounts: the SDK
      // binds to window.Pylon once and breaks if inserted twice.
      const script = document.createElement("script");
      script.id = PYLON_SCRIPT_ID;
      script.type = "text/javascript";
      script.async = true;
      script.src = `https://widget.usepylon.com/widget/${PYLON_APP_ID}`;
      document.body.appendChild(script);
    }
    // Stamp every issue opened from the widget with who opened it and from
    // which organization, so support can look them up in the admin API.
    // The slugs must match custom fields created in Pylon.
    window.Pylon?.("setNewIssueCustomFields", {
      user_id: userTid,
      organization_id: organizationTid,
    });
  }, [user, userTid, organizationTid]);

  return null;
};
